import { create } from 'zustand'
import McDataServices from '../services/McDataServices'

const useAdminStore = create((set, get) => ({
  activities: [],
  loading: false,
  error: null,
  totalElements: 0,
  totalPages: 1,
  page: 0,
  size: 10,
  sort: 'createdAt,desc',
  filters: {
    username: ''
  },
  users: [],
  usersLoading: false,
  usersTotalElements: 0,
  usersTotalPages: 1,
  usersPage: 0,
  usersSize: 10,
  usersSort: '',
  usersFilters: {
    username: ''
  },
  packages: [],
  packagesLoading: false,
  packagesTotalElements: 0,
  packagesTotalPages: 1,
  packagesPage: 0,
  packagesSize: 10,
  packagesSort: '',
  packagesFilters: {
    name: ''
  },

  actions: {
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setActivities: (activities) => set({ activities }),
    setPagination: (page, totalElements, totalPages) => set({ page, totalElements, totalPages }),
    setPage: (page) => {
      set({ page })
      get().actions.fetchActivities()
    },
    setSize: (size) => {
      set({ size, page: 0 })
      get().actions.fetchActivities()
    },
    setFilters: (filters) => {
      set((state) => ({
        filters: { ...state.filters, ...filters },
        page: 0
      }))
      get().actions.fetchActivities()
    },
    setSort: (sort) => {
      set({ sort })
      get().actions.fetchActivities()
    },
    setUsersPage: (usersPage) => {
      set({ usersPage })
      get().actions.fetchUsers()
    },
    setUsersSize: (usersSize) => {
      set({ usersSize, usersPage: 0 })
      get().actions.fetchUsers()
    },
    setUsersFilters: (usersFilters) => {
      set((state) => ({
        usersFilters: { ...state.usersFilters, ...usersFilters },
        usersPage: 0
      }))
      get().actions.fetchUsers()
    },
    setUsersSort: (usersSort) => {
      set({ usersSort })
      get().actions.fetchUsers()
    },
    setPackagesPage: (packagesPage) => {
      set({ packagesPage })
      get().actions.fetchPackages()
    },
    setPackagesSize: (packagesSize) => {
      set({ packagesSize, packagesPage: 0 })
      get().actions.fetchPackages()
    },
    setPackagesFilters: (packagesFilters) => {
      set((state) => ({
        packagesFilters: { ...state.packagesFilters, ...packagesFilters },
        packagesPage: 0
      }))
      get().actions.fetchPackages()
    },
    setPackagesSort: (packagesSort) => {
      set({ packagesSort })
      get().actions.fetchPackages()
    },
    deletePackage: async (id) => {
      set({ packagesLoading: true })
      try {
        await McDataServices.deletePackage(id)
        get().actions.fetchPackages()
      } catch (error) {
        set({ error: error?.detail || 'An error occurred', packagesLoading: false })
        throw error
      }
    },
    createPackage: async (payload) => {
      set({ packagesLoading: true })
      try {
        await McDataServices.createPackage(payload)
        get().actions.fetchPackages()
      } catch (error) {
        set({ error: error?.detail || 'An error occurred', packagesLoading: false })
      }
    },
    fetchActivities: async () => {
      const { page, size, sort, filters } = get()
      set({ loading: true, error: null })

      const payload = {
        page,
        size,
        sort,
        ...filters
      }

      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key]
        }
      })

      try {
        const response = await McDataServices.getActivities(payload)
        set({
          activities: response.content,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          loading: false
        })
      } catch (error) {
        set({ error: error?.detail || 'An error occurred', loading: false })
      }
    },
    fetchUsers: async () => {
      const { usersPage, usersSize, usersSort, usersFilters } = get()
      set({ usersLoading: true, error: null })

      const payload = {
        page: usersPage,
        pageSize: usersSize,
        sort: usersSort,
        ...usersFilters
      }

      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key]
        }
      })

      try {
        const response = await McDataServices.getUsers(payload)
        const responseData = response.data || response
        set({
          users: responseData.users || [],
          usersTotalElements: responseData.totalRecordCount || 0,
          usersTotalPages: responseData.maxPage || 1,
          usersLoading: false
        })
      } catch (error) {
        set({ error: error?.detail || 'An error occurred', usersLoading: false })
      }
    },
    fetchPackages: async () => {
      const { packagesPage, packagesSize, packagesSort, packagesFilters } = get()
      set({ packagesLoading: true, error: null })

      const payload = {
        page: packagesPage,
        pageSize: packagesSize,
        sort: packagesSort,
        ...packagesFilters
      }

      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key]
        }
      })

      try {
        const response = await McDataServices.getPackages(payload)
        const responseData = response.data || response
        set({
          packages: responseData.packages || responseData.content || [],
          packagesTotalElements: responseData.totalRecordCount || responseData.totalElements || 0,
          packagesTotalPages: responseData.maxPage || responseData.totalPages || 1,
          packagesLoading: false
        })
      } catch (error) {
        set({ error: error?.detail || 'An error occurred', packagesLoading: false })
      }
    }
  }
}))

export const useAdminStoreActions = () => useAdminStore((state) => state.actions)
export const useAdminStoreData = {
  activities: () => useAdminStore((state) => state.activities),
  loading: () => useAdminStore((state) => state.loading),
  error: () => useAdminStore((state) => state.error),
  totalElements: () => useAdminStore((state) => state.totalElements),
  totalPages: () => useAdminStore((state) => state.totalPages),
  page: () => useAdminStore((state) => state.page),
  size: () => useAdminStore((state) => state.size),
  sort: () => useAdminStore((state) => state.sort),
  filters: () => useAdminStore((state) => state.filters),
  users: () => useAdminStore((state) => state.users),
  usersLoading: () => useAdminStore((state) => state.usersLoading),
  usersTotalElements: () => useAdminStore((state) => state.usersTotalElements),
  usersTotalPages: () => useAdminStore((state) => state.usersTotalPages),
  usersPage: () => useAdminStore((state) => state.usersPage),
  usersSize: () => useAdminStore((state) => state.usersSize),
  usersSort: () => useAdminStore((state) => state.usersSort),
  usersFilters: () => useAdminStore((state) => state.usersFilters),
  packages: () => useAdminStore((state) => state.packages),
  packagesLoading: () => useAdminStore((state) => state.packagesLoading),
  packagesTotalElements: () => useAdminStore((state) => state.packagesTotalElements),
  packagesTotalPages: () => useAdminStore((state) => state.packagesTotalPages),
  packagesPage: () => useAdminStore((state) => state.packagesPage),
  packagesSize: () => useAdminStore((state) => state.packagesSize),
  packagesSort: () => useAdminStore((state) => state.packagesSort),
  packagesFilters: () => useAdminStore((state) => state.packagesFilters)
}

export default useAdminStore
